/* Kiosk Mode helpers
   =====================
   The operator may launch the POS browser with Chrome's
   `--kiosk-printing` flag, which bypasses the print dialog and routes
   any `window.print()` (and iframe-print) call straight to the system
   default printer. We can't reliably detect that flag from JS, so the
   operator opts in via a Settings toggle stored in localStorage.

   When opt-ed in, our print helpers use a HIDDEN iframe instead of a
   pop-up window — popups are visually disruptive on a POS screen, and
   in `--kiosk` (full-screen kiosk shell) Chrome blocks them entirely.

   This module is intentionally tiny — no React, just helpers. */

const STORAGE_KEY = "kiosk_print_enabled";

export const isKioskMode = () => {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return false; }
};

export const setKioskMode = (on) => {
  try { localStorage.setItem(STORAGE_KEY, on ? "1" : "0"); }
  catch { /* private mode — kiosk is moot anyway */ }
};

/** Print arbitrary HTML silently via hidden iframe.
 *  When the browser is launched with `--kiosk-printing` it prints to
 *  the default printer with NO dialog; otherwise it shows the standard
 *  preview dialog (still works, just less ergonomic).
 *  Always returns synchronously; the actual print may be async.
 */
export function printHtmlSilent(html, { afterMs = 200 } = {}) {
  // Reuse a single dedicated iframe so consecutive prints don't pile
  // up DOM nodes. Stable id lets us nuke it before re-injecting.
  let frame = document.getElementById("kiosk-print-frame");
  if (frame) frame.remove();
  frame = document.createElement("iframe");
  frame.id = "kiosk-print-frame";
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);
  frame.onload = () => {
    setTimeout(() => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch { /* noop */ }
    }, afterMs);
  };
  const doc = frame.contentDocument || frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  return { ok: true };
}

/** Print an X/Z / driver / shift REPORT.
 *
 *  Απευθύνεται στους οδηγούς και τους ταμίες που έχουν εγκατεστημένο
 *  τον τοπικό Companion agent στο 127.0.0.1:9100. Οι αναφορές παλιότερα
 *  καλούσαν κατευθείαν το `window.print()` — αυτό ανοίγει το native
 *  print dialog του browser που «κόλλαγε» ολόκληρη τη σελίδα.
 *
 *  Νέα ροή:
 *    1) Αν παρέχεται plain-text version, δοκίμασε τον Companion agent
 *       με ένα σύντομο 900ms timeout. Αν επιστρέψει ok → τυπώθηκε
 *       σιωπηλά, δεν χρειάζεται fallback.
 *    2) Διαφορετικά, fallback στο κλασικό hidden-iframe `window.print()`.
 *
 *  @param {object} opts
 *  @param {string} opts.text        — Plain text to route to Companion.
 *  @param {string} opts.html        — HTML fallback if Companion unreachable.
 *  @param {string} [opts.route]     — Companion route/profile id (printer name).
 *                                     Αν δοθεί κενό, ο agent v3 πάει στο default.
 *  @param {number} [opts.afterMs=250] — καθυστέρηση πριν το iframe print.
 *  @returns {Promise<{ok:boolean, mode:"companion"|"browser", error?:string}>}
 */
export async function printReportSmart({ text, html, route = "", afterMs = 250 } = {}) {
  const COMPANION_URL = "http://127.0.0.1:9100";
  if (text && text.length > 0) {
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 900);
      const id = (route || "").trim();
      const res = await fetch(`${COMPANION_URL}/print-ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ route: id, profile: id, printer: id, text }),
        signal: ctrl.signal,
      });
      clearTimeout(tid);
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        if (j && j.ok) return { ok: true, mode: "companion" };
      }
    } catch { /* companion unreachable — fall through */ }
  }
  if (html) {
    printHtmlSilent(html, { afterMs });
    return { ok: true, mode: "browser" };
  }
  return { ok: false, mode: "browser", error: "no-content" };
}
